import {
  DOSYA_BELGE_AILESI,
  DOSYA_GIRDI_TUR_ADI,
  GIRDI_SEKME_ADI,
  GIRDI_YETKILERI,
  OEM_SAYFA_YERI_ADI,
  ROL_ETIKET,
  YETKI,
  dosyaBelgeHazirla,
  girdiCakismaHatasi,
  girdiDosyasiHatasi,
  girdiIcerikTurleri,
  girdiKapsamlari,
  girdiTekilMi,
  girdiYetkiliMi,
  inspektorSecimleri,
  isEmriHatasi,
  izBagiHazirla,
  kokenYazimi,
  setHatMi,
  yetkiMetni,
  type AletSecenegi,
  type DosyaBelgeGovdesi,
  type DosyaBelgeSatiri,
  type DosyaGirdiTuru,
  type DosyaYuklemeYaniti,
  type GirdiIslemi,
  type GirdiKapsami,
  type GirdiSekmesi,
  type GirdiTurGrubu,
  type InspektorSecenegi,
  type Islem,
  type IzBagiGovdesi,
  type KokenGovdesi,
  type KokenParcasi,
  type KokenSatiri,
  type OemSayfaSatiri,
  type OemSayfaYeri,
  type ProjeSekmesiYaniti,
  type RaporSatiri,
  type RiserSekmesiYaniti,
  type Rol,
  type SayfaAraligi,
  type SetHat,
} from "@oasis/domain";
import { cevir } from "@/lib/dil";
import { cipMetni, hatAdi, kaynakNolari, satirAdi, satirEkrani, type Ton } from "@/lib/riser-belgeleri";

/**
 * D-059: Project documents ekranının saf yardımcıları. Kurallar alan paketindedir (tür × kapsam, yetki, denetimler); burada ekrandaki
 * seçenek listeleri, metinler ve kaydetmeden önceki denetimler o kurallardan kurulur. Sunucu hazır cümle göndermez: tür, sekme ve
 * satır adları alan paketinde İngilizce sabittir, burada `cevir(degisken)` ile gösterilir (Türkçeleri tr-elle.tsv'de). Denetimlerin
 * döndürdüğü hata İngilizce cümledir; ekran `cevir(hata)` ile gösterir (sunucunun aynı kural için döndürdüğü cümleyle aynı).
 * JSX yok; tarih biçimi dışarıdan verilir: modül React ve oturum katmanını yüklemez.
 * D-062 / D-063: "NDT personnel" sekmesinin yardımcıları (personel kodu, sayaç, tablo) kalktı; raporun imzalayanı Registers › NDT
 * inspectors kütüğündeki inspektörlerden adıyla seçilir (`imzalayanCipleri`, `inspektorSecenekleri`).
 */

/* ---------------------------------------------------------------- uç yolları ---------------------------------------------------------------- */

const p = encodeURIComponent;
/** GET: sekme 1 ile riser seçici · POST: bağ ekleme. */
export const ANA_YOL = "/dosya-belge";
/** POST (form-data: dosya + tur): dosyayı yükler, bağ yazmaz. */
export const YUKLE_YOLU = `${ANA_YOL}/yukle`;
export const kaldirYolu = (id: string) => `${ANA_YOL}/${p(id)}/kaldir`;
/** GET: sekme 2. */
export const riserYolu = (rn: string) => `${ANA_YOL}/riser/${p(rn)}`;
export const kokenYolu = (rn: string) => `${riserYolu(rn)}/koken`;
export const oemSayfaYolu = (rn: string) => `${riserYolu(rn)}/oem-sayfa`;
export const izYolu = (dokumanId: string) => `${ANA_YOL}/dokuman/${p(dokumanId)}/iz`;

/* ---------------------------------------------------------------- sekmeler, adlar, yetki ---------------------------------------------------------------- */

/** Açılışta seçili sekme: adreste riser varsa "Riser", yoksa "Project and work order". */
export const ilkSekme = (rn: string | null | undefined): GirdiSekmesi => (rn ? "riser" : "proje");
export const sekmeAdi = (s: GirdiSekmesi): string => cevir(GIRDI_SEKME_ADI[s]);
/** Türün bu ekrandaki adı ("Identification plate photos"); Riser Documentation'daki satır adı ayrıdır. */
export const turAdi = (tur: DosyaGirdiTuru): string => cevir(DOSYA_GIRDI_TUR_ADI[tur]);

/** Kod sırası: içindeki sayılar sayı olarak karşılaştırılır (R9, R10'dan; UT-2, UT-10'dan önce). */
const dogalSira = (a: string, b: string) => a.localeCompare(b, "en", { numeric: true });

/**
 * İşlemi yapabilen roller, `Buton`un ipucuyla aynı biçimde ("Yetki: Field Inspector / Quality Lead"). İşlem birden çok yetkiyle
 * yapılabildiği için (kimlik plakası fotoğrafı ve rapor bağı: `evrak` ya da `mrb`) roller GIRDI_YETKILERI'nden toplanır.
 */
export function girdiYetkiMetni(islem: GirdiIslemi): string {
  const [ilk, ...kalan] = GIRDI_YETKILERI[islem] as readonly Islem[];
  const etiket = (r: Rol) => ROL_ETIKET[r];
  const roller = new Set<Rol>(YETKI[ilk!]);
  const ilkSayi = roller.size;
  for (const i of kalan) for (const r of YETKI[i] as readonly Rol[]) roller.add(r);
  return [yetkiMetni(ilk!, etiket), ...[...roller].slice(ilkSayi).map(etiket)].join(" / ");
}

/** Rol işlemi yapabilir mi; yapamıyorsa düğmenin ipucu (düğme pasif çizilir). */
export function girdiYetkisi(rol: Rol, islem: GirdiIslemi): { yetkili: boolean; ipucu: string | undefined } {
  const yetkili = girdiYetkiliMi(rol, islem);
  return { yetkili, ipucu: yetkili ? undefined : girdiYetkiMetni(islem) };
}

/* ---------------------------------------------------------------- "Applies to" ve sayfalar ---------------------------------------------------------------- */

/** "1 riser" / "38 risers". Sayı ayrı yazılır: "{0} risers" kalıbı sözlüğe girerse sunucudan gelen başka metinleri yarım çevirir. */
export const riserAdedi = (n: number): string => (n === 1 ? `1 ${cevir("riser")}` : `${n} ${cevir("risers")}`);

/** "Applies to" seçeneği: türün izinli kapsamlarından; iş emri numarası elle yazılmaz, bilinen numaralardan seçilir. */
export interface KapsamSecenegi {
  /** Seçim kutusunun değeri: "proje" ya da "is_emri:<no>". */
  deger: string;
  kapsam: GirdiKapsami;
  anahtar: string | null;
  etiket: string;
  /** Seçenek kaç risere uygulanır ("Applies to {0} risers."). */
  riserSayisi: number;
}

/** "Applies to" seçenekleri, statik sayfadaki sırayla: önce bilinen iş emirleri (numara sırasıyla), sonra "All risers (project)". */
export function kapsamSecenekleri(tur: DosyaGirdiTuru, v: Pick<ProjeSekmesiYaniti, "isEmirleri" | "riserSayisi">): KapsamSecenegi[] {
  const izinli = girdiKapsamlari(tur);
  const s: KapsamSecenegi[] = [];
  if (izinli.includes("is_emri")) {
    for (const e of v.isEmirleri) s.push({ deger: `is_emri:${e.no}`, kapsam: "is_emri", anahtar: e.no, etiket: `${cevir("Work order")} ${e.no}`, riserSayisi: e.riserSayisi });
  }
  if (izinli.includes("proje")) s.push({ deger: "proje", kapsam: "proje", anahtar: null, etiket: cevir("All risers (project)"), riserSayisi: v.riserSayisi });
  return s;
}

/** Seçim kutusunun altındaki satır: "Applies to 38 risers."; hiçbir risere uygulanmıyorsa uyarı tonunda (belge hiçbir riserde görünmez). */
export function uygulananMetin(n: number): { metin: string; uyari: boolean } {
  return { metin: n === 1 ? cevir("Applies to 1 riser.") : cevir("Applies to {0} risers.", n), uyari: n === 0 };
}

/** Tablonun "Applies to" hücresi: çip ("Work order 452909" | "Project") ve yanındaki metin ("38 risers" | "all risers"). */
export function kapsamCipi(s: Pick<DosyaBelgeSatiri, "kapsam" | "anahtar" | "riserSayisi">): { cip: string; ek: string } {
  if (s.kapsam === "is_emri") return { cip: [cevir("Work order"), s.anahtar].filter(Boolean).join(" "), ek: riserAdedi(s.riserSayisi ?? 0) };
  return { cip: cevir("Project"), ek: cevir("all risers") };
}

const SAYFA_BICIMI = /^(\d+)\s*(?:[-–—]\s*(\d+))?$/;

/**
 * "Pages" kutusu → bağ gövdesinin sayfaları: boş = dosyanın tamamı (ikisi de null) · "9" = 9–9 · "1-6" = 1–6. Okunamayan metinde
 * ikisi de NaN döner: `dosyaBelgeHazirla` tam sayı olmayan sayfayı kendi cümlesiyle reddeder (burada ayrı hata metni yazılmaz).
 */
export function sayfalariOku(metin: string): { sayfaBas: number | null; sayfaSon: number | null } {
  const s = metin.trim();
  if (!s) return { sayfaBas: null, sayfaSon: null };
  const m = SAYFA_BICIMI.exec(s);
  if (!m) return { sayfaBas: Number.NaN, sayfaSon: Number.NaN };
  return { sayfaBas: Number(m[1]), sayfaSon: Number(m[2] ?? m[1]) };
}

/** Sayfa aralığının yazımı: "9" · "1–6"; `ayrac` "-" verilirse kutuya yazılan biçim ("1-6"). Aralık yoksa boş. */
export const aralikMetni = (a: SayfaAraligi | null | undefined, ayrac = "–"): string => (a ? (a.sayfaBas === a.sayfaSon ? `${a.sayfaBas}` : `${a.sayfaBas}${ayrac}${a.sayfaSon}`) : "");

const satirAraligi = (s: Pick<DosyaBelgeSatiri, "sayfaBas" | "sayfaSon">): SayfaAraligi | null =>
  s.sayfaBas !== null && s.sayfaSon !== null ? { sayfaBas: s.sayfaBas, sayfaSon: s.sayfaSon } : null;

/** Tablonun "Pages" hücresi: "all" (dosyanın tamamı) · "9" · "1–6". */
export const sayfaHucresi = (s: Pick<DosyaBelgeSatiri, "sayfaBas" | "sayfaSon">): string => aralikMetni(satirAraligi(s)) || cevir("all");

/* ---------------------------------------------------------------- "+ Add document" penceresi ---------------------------------------------------------------- */

/** Pencerenin alanları. `dosya`: "Upload PDF" ile yüklenmiş dosya (henüz bağlanmadı); `sayfalar` ve `not` kutulardaki metin. */
export interface PencereGirdisi {
  tur: DosyaGirdiTuru;
  secenek: Pick<KapsamSecenegi, "kapsam" | "anahtar"> | null;
  dosya: DosyaYuklemeYaniti | null;
  sayfalar: string;
  not: string;
}

type EtkinBag = Pick<DosyaBelgeSatiri, "kapsam" | "anahtar" | "tur" | "dokumanId" | "sayfaBas" | "sayfaSon">;

/**
 * Kaydetmeden önceki denetim, sunucunun uyguladığı kurallarla aynı fonksiyonlardan: tür × kapsam, dosya seçili mi, sayfalar
 * (`dosyaBelgeHazirla`), iş emri bilinen numaralardan mı (`isEmriHatasi`), sayfa PDF'in içinde mi (`girdiDosyasiHatasi`), tekil türde
 * ya da aynı dosya ve sayfalarla ikinci bağ (`girdiCakismaHatasi`). Geçerse gönderilecek gövde döner; hata İngilizce cümledir.
 */
export function pencereDenetimi(g: PencereGirdisi, o: { isEmirleri: readonly string[]; etkin: readonly EtkinBag[] }): { govde: DosyaBelgeGovdesi | null; hata: string | null } {
  const hata = (m: string) => ({ govde: null, hata: m });
  const h = dosyaBelgeHazirla({ kapsam: g.secenek?.kapsam, anahtar: g.secenek?.anahtar, tur: g.tur, dokumanId: g.dosya?.id ?? "", ...sayfalariOku(g.sayfalar), not: g.not });
  if (!h.taslak) return hata(h.hata ?? "Error");
  const t = h.taslak;
  if (t.kapsam === "is_emri") {
    const isEmri = isEmriHatasi(t.anahtar, o.isEmirleri);
    if (isEmri) return hata(isEmri);
  }
  // Dosya bu ekrandan yüklendi; yalnız PDF kabul eden türde içerik yüklemede denetlendi (ad ".pdf" ile bitmese de PDF'tir).
  const pdf = girdiIcerikTurleri(t.tur).every((x) => x === "pdf") || /\.pdf$/i.test(g.dosya?.ad ?? "");
  const dosya = girdiDosyasiHatasi(t, g.dosya ? { aile: DOSYA_BELGE_AILESI, pdf, sayfaSayisi: g.dosya.toplam || null } : null);
  if (dosya) return hata(dosya);
  const cakisma = girdiCakismaHatasi(t, o.etkin.map((b) => ({ ...b, dokumanId: b.dokumanId ?? "" })));
  if (cakisma) return hata(cakisma);
  return { govde: { kapsam: t.kapsam, anahtar: t.anahtar, tur: t.tur, dokumanId: t.dokumanId, sayfaBas: t.sayfaBas, sayfaSon: t.sayfaSon, not: t.not }, hata: null };
}

/* ---------------------------------------------------------------- belge satırı ---------------------------------------------------------------- */

/** Belge adının çizimi: `dokumanId` doluysa ad bağlantıdır (sayfa aralığı varsa o sayfadan açılır), null ise düz metin. */
export interface BelgeGorunumu {
  metin: string;
  dokumanId: string | null;
  sayfa: SayfaAraligi | null;
}

/**
 * Belgenin ekrandaki adı ve bağlantı kararı. Dosya adı gelmeyen belgede (fiyat taşıyan belgeyi açamayan rol, dosya kaydı
 * bulunamayan bağ) tür adı yazılır. Bağlantı yalnız sunucu belge kimliğini verdiyse kurulur (erişim kuralı alan paketinde).
 * D-063: kişisel sınıfta girdi türü kalmadı (NDT sertifikaları Registers › NDT inspectors'ta); o türe özgü dal kalktı.
 */
export function belgeGorunumu(s: Pick<DosyaBelgeSatiri, "tur" | "dokumanId" | "dosyaAd" | "sayfaBas" | "sayfaSon">): BelgeGorunumu {
  return { metin: s.dosyaAd || turAdi(s.tur), dokumanId: s.dokumanId, sayfa: satirAraligi(s) };
}

/** "Uploaded" hücresi: yükleyen ve tarih. */
export const yuklemeDamgasi = (s: Pick<DosyaBelgeSatiri, "kim" | "ts">, tarih: (iso: string) => string): string => `${s.kim} · ${tarih(s.ts)}`;

/* ---------------------------------------------------------------- "Riser documents" ---------------------------------------------------------------- */

/** Belgesi olmayan türün alt metni: "Not uploaded." ve tekil iki belgede kimin, ne zaman düzenlediği. */
export function yuklenmediMetni(tur: DosyaGirdiTuru): string {
  const ek = tur === "osi_coc" ? cevir("One per riser; issued by Oil States at the end.") : tur === "dnv_survey_report" ? cevir("One per riser; issued by DNV at the end.") : "";
  return [cevir("Not uploaded."), ek].filter(Boolean).join(" ");
}

/**
 * Tür satırının yükleme düğmesi. Tekil türde (CoC, DNV Survey Report) belge varken düğme yoktur: yenisi için önce eskisi kaldırılır.
 * Kimlik plakasında "+ Add photo"; öteki türlerde ilk belge "Upload PDF", sonrakiler "+ Add document".
 */
export function yuklemeDugmesi(g: { tur: GirdiTurGrubu["tur"]; satirlar: readonly unknown[] }): string | null {
  if (g.satirlar.length && girdiTekilMi(g.tur)) return null;
  if (g.tur === "kimlik_plakasi") return cevir("+ Add photo");
  return g.satirlar.length ? cevir("+ Add document") : cevir("Upload PDF");
}

/**
 * Tür satırının alt metni için belgeler ve damgaları. Art arda gelen belgelerin yükleyeni ve tarihi aynıysa damga yalnız sonuncunun
 * ardından yazılır ("a.jpg · b.jpg · Field Inspector · 01/10/2026"); farklıysa her belgenin ardından.
 */
export function damgaParcalari<T extends Pick<DosyaBelgeSatiri, "kim" | "ts">>(satirlar: readonly T[], tarih: (iso: string) => string): { satir: T; damga: string | null }[] {
  return satirlar.map((s, i) => {
    const damga = yuklemeDamgasi(s, tarih);
    const sonraki = satirlar[i + 1];
    return { satir: s, damga: sonraki && yuklemeDamgasi(sonraki, tarih) === damga ? null : damga };
  });
}

/* ---------------------------------------------------------------- köken ---------------------------------------------------------------- */

export interface Secenek {
  deger: string;
  etiket: string;
}

/** "another riser…" seçeneğinin değeri: seçilince R### girişi açılır (OASIS'te olmayan riser de yazılabilir). */
export const BASKA_RISER = "__baska";

/**
 * Köken seçenekleri: "this riser" (değer boş) · OASIS'teki öteki riserler, RN sırasıyla · "another riser…". Riserin kendi RN'i
 * listede yer almaz (riser kendi kendisinin vericisi olamaz). Seçili riser listede yoksa (elle yazılmış, OASIS'te olmayan riser)
 * sırasına eklenir: seçim kutusu seçili değeri gösterebilsin.
 */
export function kokenSecenekleri(rn: string, riserler: readonly string[], secili: string | null): Secenek[] {
  const rnler = [...new Set([...riserler, ...(secili ? [secili] : [])])].filter((x) => x !== rn).sort(dogalSira);
  return [{ deger: "", etiket: cevir("this riser") }, ...rnler.map((x) => ({ deger: x, etiket: x })), { deger: BASKA_RISER, etiket: cevir("another riser…") }];
}

/** Köken tablosunun hücresi: seçim kutusu (seçili riser; null = "this riser") ya da "From riser {0}" çipi. */
export type KokenHucresi = { tip: "secim"; secili: string | null } | { tip: "cip"; rn: string };

/** Hattın tamamı başka riserden geliyorsa box, pipe ve pin ona uyar: seçim kutusu yerine çip. "Whole line" hücresi hep seçim kutusudur. */
export function kokenHucresi(s: KokenSatiri, parca: KokenParcasi): KokenHucresi {
  if (parca === "hat") return { tip: "secim", secili: s.hatRn };
  return s.hatRn ? { tip: "cip", rn: s.hatRn } : { tip: "secim", secili: s[parca] };
}

/**
 * Köken seçiminin göndermeden önceki denetimi (`kokenYazimi`nin veri gerektirmeyen kuralları): RN biçimi ("120" → "R120") ve riserin
 * kendi kendisinin vericisi olamaması. Boş değer "this riser"dır (`kokenRn: null`). Hattın tamamı kuralını ekran çiple, sunucu
 * etkin satırlarla uygular.
 */
export function kokenDenetimi(rn: string, hat: SetHat, parca: KokenParcasi, ham: string): { govde: KokenGovdesi | null; hata: string | null } {
  const y = kokenYazimi(rn, [], { hat, parca, kokenRn: ham });
  if (y.hata) return { govde: null, hata: y.hata };
  return { govde: { hat, parca, kokenRn: y.eklenecek?.kokenRn ?? null }, hata: null };
}

/* ---------------------------------------------------------------- OEM kaydı sayfaları ---------------------------------------------------------------- */

/** "Part of the book" hücresi: hat adı (çevrilmez) ya da DNV belgesinin adı. */
export const oemYerAdi = (yer: OemSayfaYeri): string => (setHatMi(yer) ? hatAdi(yer) : cevir(OEM_SAYFA_YERI_ADI[yer]));

/** Hesap çıkmayan satırda düzeltme kutusunun örneği. */
const ORNEK_ARALIK = "31-96";

/** OEM satırının çizimi. */
export interface OemGorunumu {
  ad: string;
  /** "Pages" hücresi: geçerli aralık ("31–96"); hattın tamamı başka riserden geliyorsa "OEM MRB of R120" (aralık biliniyorsa ardından); yoksa tire. */
  sayfa: string;
  /** Düzeltme kutusunun değeri: elle girilmiş aralık ("97-138"), yoksa boş. */
  duzeltme: string;
  /** Kutunun örnek metni: iç dizinden hesaplanan aralık ("e.g. 31-96"). */
  ornek: string;
  kaynak: { ton: Ton; metin: string };
  /**
   * Kutu neden pasif: "koken" = hattın tamamı başka riserden geliyor (düzeltme vericinin kendi sayfasında yapılır) · "kitap_yok" =
   * riserin MRB kaydı ya da PDF'i yok (düzeltme kitabın PDF'ine yazılır). null = düzeltme girilebilir (yetki ayrıca denetlenir).
   */
  pasif: "koken" | "kitap_yok" | null;
}

export function oemGorunumu(s: OemSayfaSatiri, kitap: RiserSekmesiYaniti["oem"]["kitap"]): OemGorunumu {
  const aralik = aralikMetni(s.sayfa);
  return {
    ad: oemYerAdi(s.yer),
    sayfa: s.kokenRn ? [cevir("OEM MRB of {0}", s.kokenRn), aralik].filter(Boolean).join(" · ") : aralik || "—",
    duzeltme: aralikMetni(s.elle, "-"),
    ornek: cevir("e.g. {0}", aralikMetni(s.hesap, "-") || ORNEK_ARALIK),
    kaynak: s.kaynak === "elle" ? { ton: "warn", metin: cevir("set by hand") } : { ton: "neutral", metin: cevir("from the document index") },
    pasif: s.kokenRn ? "koken" : kitap?.dokumanId ? null : "kitap_yok",
  };
}

/**
 * Düzeltme kutusu odaktan çıkınca kaydedilsin mi: kutudaki aralık kayıtlı elle aralıktan farklıysa ("97 – 138" ile "97-138" aynıdır;
 * boş kutu = elle aralık yok). Okunamayan metin de gönderilir: biçim hatasını sunucu kendi cümlesiyle söyler.
 */
export function oemDegistiMi(metin: string, elle: SayfaAraligi | null): boolean {
  const y = sayfalariOku(metin);
  return y.sayfaBas !== (elle?.sayfaBas ?? null) || y.sayfaSon !== (elle?.sayfaSon ?? null);
}

/* ---------------------------------------------------------------- raporlar: test ekipmanı ve imzalayan ---------------------------------------------------------------- */

/** "n reports on file for R002" */
export const raporSayaci = (n: number, rn: string): string => (n === 1 ? cevir("1 report on file for {0}", rn) : cevir("{0} reports on file for {1}", n, rn));

/** Raporun adı ve kapsam çipi: Riser Documentation'daki satırla aynı ("Pressure Test Certificate and chart", "Line: Choke"). */
export const raporAdi = (r: Pick<RaporSatiri, "satir">): string => satirAdi(r.satir);
export const raporCipi = (r: Pick<RaporSatiri, "satir">): string => cipMetni(r.satir);

/**
 * Rapor satırının alt metni üç parçadır: dosya adından önce adım adı ve (iş ya da kaynak kapsamlı raporda) hat ve kaynak numaraları;
 * dosya adı (bağlantı); sonra yüklendiği ekran. Dosya adı gelmediyse bağlantının metni raporun adıdır.
 */
export function raporAltMetni(r: Pick<RaporSatiri, "satir">): { once: string[]; dosyaAd: string; sonra: string[] } {
  const s = r.satir;
  const once: string[] = [];
  if (s.adimAd) once.push(cevir(s.adimAd));
  if ((s.kapsam === "is" || s.kapsam === "kaynak") && s.hat) once.push([hatAdi(s.hat), kaynakNolari(s.kaynakNolar)].filter(Boolean).join(" "));
  return { once, dosyaAd: s.dosyaAd || raporAdi(r), sonra: [satirEkrani(s)].filter(Boolean) };
}

/** Basınç testi ekipmanının kategorisi (Registers › Instruments): seçim listesinde başa gelir. */
const TEST_EKIPMANI_KATEGORISI = "Pressure";

/**
 * Alet kütüğünün seçim sırası: önce basınç testi ekipmanı ("Pressure"), sonra öteki kategoriler ad sırasıyla, kategorisi olmayanlar
 * sonda; kategori içinde kod sırası.
 */
export function aletSirasi<T extends Pick<AletSecenegi, "kod" | "kategori">>(aletler: readonly T[]): T[] {
  const grup = (a: T) => (a.kategori === TEST_EKIPMANI_KATEGORISI ? 0 : a.kategori ? 1 : 2);
  return [...aletler].sort((a, b) => grup(a) - grup(b) || dogalSira(a.kategori ?? "", b.kategori ?? "") || dogalSira(a.kod, b.kod));
}

/** Aletin adı: "CAL-01 calibrator". Kütükte olmayan kod olduğu gibi; seçili pasif alet " · inactive" ekiyle. */
export function aletEtiketi(kod: string, aletler: readonly AletSecenegi[]): string {
  const a = aletler.find((x) => x.kod === kod);
  return a ? `${a.kod} ${a.ad}${a.aktif ? "" : cevir(" · inactive")}` : kod;
}

/** "Test equipment" seçeneği; `grup` kategoridir (seçim kutusunda başlık). */
export interface AletSecimi extends Secenek {
  grup: string | null;
}

/** Eklenebilecek aletler, `aletSirasi` ile: pasif alet ve zaten seçili alet sunulmaz (seçili pasif alet çipte kalır). */
export function aletSecenekleri(aletler: readonly AletSecenegi[], secili: readonly string[]): AletSecimi[] {
  return aletSirasi(aletler)
    .filter((a) => a.aktif && !secili.includes(a.kod))
    .map((a) => ({ deger: a.kod, etiket: `${a.kod} ${a.ad}`, grup: a.kategori }));
}

/** Seçenekleri art arda gelen gruplarına böler (seçim kutusunda kategori başlıkları). */
export function gruplaraBol<T extends { grup: string | null }>(secenekler: readonly T[]): { grup: string | null; secenekler: T[] }[] {
  const sonuc: { grup: string | null; secenekler: T[] }[] = [];
  for (const s of secenekler) {
    const son = sonuc[sonuc.length - 1];
    if (son && son.grup === s.grup) son.secenekler.push(s);
    else sonuc.push({ grup: s.grup, secenekler: [s] });
  }
  return sonuc;
}

/**
 * D-062: inspektörün çipteki adı: "Deniz Örnek"; seçili pasif inspektör " · inactive" ekiyle. Kütükte olmayan kimlikte null:
 * inspektörün sistem kimliği hiçbir zaman ekrana yazılmaz (çip çizilmez).
 */
export function inspektorEtiketi(id: string, inspektorler: readonly InspektorSecenegi[]): string | null {
  const k = inspektorler.find((x) => x.id === id);
  return k ? `${k.ad}${k.aktif ? "" : cevir(" · inactive")}` : null;
}

/** "Signed by" hücresinin çipi: `id` bağ gövdesine gider, `etiket` ekrana yazılır. */
export interface ImzalayanCipi {
  id: string;
  etiket: string;
}

/**
 * D-062: raporun imzalayan çipleri, seçim sırasıyla. Kütükte olmayan kimlik gösterilmez (silme ile yükleme yarışı, ham içe aktarım);
 * sonraki ilk yazımda listeden düşer (`izBagiHazirla`). Sonuç boşsa rapor "signer missing" sayılır (`imzalayanEksikMi`).
 */
export function imzalayanCipleri(secili: readonly string[], inspektorler: readonly InspektorSecenegi[]): ImzalayanCipi[] {
  return secili.flatMap((id): ImzalayanCipi[] => {
    const etiket = inspektorEtiketi(id, inspektorler);
    return etiket === null ? [] : [{ id, etiket }];
  });
}

/**
 * D-062: NDT raporunun imzalayanı eksik mi (sarı "signer missing"): gösterilebilen imzalayanı yoksa. Basınç testi sertifikasında
 * imzalayan sorulmaz ("—"): eksik sayılmaz.
 */
export function imzalayanEksikMi(r: Pick<RaporSatiri, "tip" | "imzalayanlar">, inspektorler: readonly InspektorSecenegi[]): boolean {
  return r.tip === "ndt" && imzalayanCipleri(r.imzalayanlar, inspektorler).length === 0;
}

/**
 * D-062: "Signed by" seçenekleri ("+ Add"): kütükteki aktif ve henüz seçili olmayan inspektörler, ad sırasıyla (kural alan
 * paketinde: `inspektorSecimleri`). Değer inspektörün kimliği, etiket adıdır. Serbest giriş yoktur: listede olmayan inspektörü önce
 * PM ya da sistem yöneticisi Registers › NDT inspectors'a ekler.
 */
export function inspektorSecenekleri(inspektorler: readonly InspektorSecenegi[], secili: readonly string[]): Secenek[] {
  return inspektorSecimleri(inspektorler, secili).map((k) => ({ deger: k.id, etiket: k.ad }));
}

/**
 * Ekipman / imzalayan bağının göndermeden önceki denetimi (`izBagiHazirla`: ekipman yalnız basınç testi sertifikasına, imzalayan
 * yalnız NDT raporuna; alet kütükte ve aktif). Değişiklik yoksa gövde de hata da null: istek atılmaz.
 * D-062: imzalayan inspektörün kimliğiyle gider (`ndtInspektor`) ve inspektör kütüğüyle denetlenir: kütükte olmayan kimlik
 * reddedilir, pasif inspektör yeni seçilemez, zaten seçili pasif inspektör kalır; önceden seçili olup kütükte artık olmayan kimlik
 * gövdeden sessizce düşer. Verilen liste öncekinin yerine geçer (imzalayan buradan eklenir ve çıkarılır).
 */
export function izDenetimi(
  r: Pick<RaporSatiri, "tip" | "aletler" | "imzalayanlar">,
  govde: IzBagiGovdesi,
  aletler: readonly AletSecenegi[],
  inspektorler: readonly InspektorSecenegi[],
): { govde: IzBagiGovdesi | null; hata: string | null } {
  const y = izBagiHazirla({ tip: r.tip, onceki: { aletler: r.aletler, ndtInspektor: r.imzalayanlar }, govde, aletKutugu: aletler, inspektorKutugu: inspektorler });
  if (y.hata) return { govde: null, hata: y.hata };
  if (!y.degisti) return { govde: null, hata: null };
  return { govde: govde.aletler !== undefined ? { aletler: y.aletler } : { ndtInspektor: y.ndtInspektor }, hata: null };
}
